import { db } from '~/config/firebase.config';
import {
    collection,
    doc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    getDoc,
    deleteDoc,
    QuerySnapshot,
    DocumentData,
} from 'firebase/firestore';

/**
 * Playlists générées par IA (unblind-test), persistées sur le compte client.
 *
 * Même modèle de confiance que QuizzesAPI : le jeu tourne dans une iframe sans
 * Firebase ni authentification, il envoie un postMessage et c'est la console
 * (authentifiée, porteuse de l'utilisateur) qui écrit. Le chemin passe sous
 * `clients/{clientID}/games/**`, déjà couvert par les règles Firestore
 * existantes — aucune règle à ajouter.
 */

/** Piste telle que persistée : le Track du jeu SANS `preview_url`. */
export type StoredPlaylistTrack = {
    id: string | number;
    name: string;
    artist_name: string;
    href?: string;
    artist_href?: string;
    popularity?: number;
    picture?: string;
    work?: string;
};

export type StoredPlaylist = {
    id: string;
    name: string;
    prompt: string;
    tracks: StoredPlaylistTrack[];
    createdBy?: string;
    createdAt: Date;
    updatedAt: Date;
};

/**
 * Les URLs de preview Deezer sont signées et expirent ~15 min après émission :
 * les persister n'a aucun intérêt, le jeu les re-résout de toute façon à la
 * lecture (DeezerAPI.refreshTrackPreviews / getFreshPreviewUrl). On les retire
 * donc à l'écriture pour ne pas stocker de données mortes.
 */
function toStoredTrack(track: Record<string, any>): StoredPlaylistTrack {
    const stored: StoredPlaylistTrack = {
        id: track.id,
        name: track.name,
        artist_name: track.artist_name,
    };
    if (track.href !== undefined) stored.href = track.href;
    if (track.artist_href !== undefined) stored.artist_href = track.artist_href;
    if (track.popularity !== undefined) stored.popularity = track.popularity;
    if (track.picture !== undefined) stored.picture = track.picture;
    if (track.work !== undefined) stored.work = track.work;
    return stored;
}

const getPlaylistsRef = (clientID: string, gameID: string) =>
    collection(db, 'clients', clientID, 'games', gameID, 'aiPlaylists');

const getPlaylistRef = (clientID: string, gameID: string, playlistId: string) =>
    doc(db, 'clients', clientID, 'games', gameID, 'aiPlaylists', playlistId);

function toDate(value: any): Date {
    return value && typeof value.toDate === 'function' ? value.toDate() : new Date();
}

/**
 * Enregistre (ou met à jour) une playlist IA.
 * Appelé par la console (authentifiée) quand le jeu envoie un postMessage.
 */
async function savePlaylist(
    clientID: string,
    gameID: string,
    playlistId: string,
    playlistData: { name: string; prompt?: string; tracks: Record<string, any>[]; createdBy?: string }
): Promise<void> {
    const playlistRef = getPlaylistRef(clientID, gameID, playlistId);

    const dataToSave: Record<string, any> = {
        name: playlistData.name,
        prompt: playlistData.prompt ?? '',
        tracks: (playlistData.tracks ?? []).map(toStoredTrack),
        updatedAt: serverTimestamp(),
    };
    if (playlistData.createdBy !== undefined) {
        dataToSave.createdBy = playlistData.createdBy;
    }

    try {
        const document = await getDoc(playlistRef);
        if (document.exists()) {
            await updateDoc(playlistRef, dataToSave);
        } else {
            await setDoc(playlistRef, { ...dataToSave, createdAt: serverTimestamp() });
        }
    } catch (err) {
        console.error('Cannot save AI playlist:', err);
        throw err;
    }
}

/** Récupère toutes les playlists IA du client, les plus récentes d'abord. */
async function retrievePlaylists(clientID: string, gameID: string, total: number = 0): Promise<StoredPlaylist[]> {
    const limitValue = total <= 0 ? 10000 : total;
    const q = query(getPlaylistsRef(clientID, gameID), orderBy('updatedAt', 'desc'), limit(limitValue));

    try {
        const querySnapshot = await getDocs(q);
        const playlists: StoredPlaylist[] = [];
        querySnapshot.forEach((document) => {
            const data = document.data();
            playlists.push({
                id: document.id,
                name: data.name,
                prompt: data.prompt ?? '',
                tracks: data.tracks ?? [],
                createdBy: data.createdBy,
                createdAt: toDate(data.createdAt),
                updatedAt: toDate(data.updatedAt),
            });
        });
        return playlists;
    } catch (err) {
        console.error('Cannot retrieve AI playlists:', err);
        throw err;
    }
}

async function deletePlaylist(clientID: string, gameID: string, playlistId: string): Promise<void> {
    try {
        await deleteDoc(getPlaylistRef(clientID, gameID, playlistId));
    } catch (err) {
        console.error('Cannot delete AI playlist:', err);
        throw err;
    }
}

/**
 * Souscrit aux changements pour pousser la liste à jour vers le jeu / le cockpit.
 * Le snapshot est transmis au callback : l'appelant peut ignorer les échos
 * (writes locaux non confirmés, ré-émissions sans changement de docs).
 */
function subscribeToPlaylists(clientID: string, gameID: string) {
    const playlistsRef = getPlaylistsRef(clientID, gameID);
    return (onValueChange: (snapshot: QuerySnapshot<DocumentData>) => void) => onSnapshot(playlistsRef, onValueChange);
}

export const PlaylistsAPI = {
    savePlaylist,
    retrievePlaylists,
    deletePlaylist,
    subscribeToPlaylists,
    ref: getPlaylistsRef,
};
